import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, Check, Youtube, Instagram, Film, ImagePlus, Upload } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, mediaUrl } from "@/lib/api";

const inp = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-magenta";

const detectType = (url) => {
  if (/(?:youtube\.com\/(?:watch\?.*v=|shorts\/|embed\/|live\/)|youtu\.be\/)([\w-]{6,})/.test(url || "")) return "youtube";
  if (/instagram\.com\/(?:reel|reels|p)\/([\w-]+)/.test(url || "")) return "instagram";
  return "";
};

const EMPTY_FORM = { url: "", title: "", category: "", thumbnail: "", type: "" };

export default function SocialGalleryManager() {
  const [data, setData] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [renaming, setRenaming] = useState(null); // {id, name}
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [media, setMedia] = useState([]);
  const fileRef = useRef(null);
  const thumbRef = useRef(null);

  const load = () => api.get("/videos").then((r) => setData(r.data)).catch(() => setData({ categories: [], videos: [] }));
  useEffect(() => { load(); }, []);

  const cats = data?.categories || [];
  const videos = data?.videos || [];

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/videos/categories", { name: newCat.trim() });
      setNewCat("");
      toast.success("Category added");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add category"); }
  };

  const saveRename = async () => {
    try {
      await api.put(`/videos/categories/${renaming.id}`, { name: renaming.name.trim() });
      setRenaming(null);
      toast.success("Category renamed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rename"); }
  };

  const removeCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Videos inside it will also be removed.`)) return;
    await api.delete(`/videos/categories/${c.id}`);
    toast.success("Category deleted");
    load();
  };

  const submitVideo = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error("Pick a category first"); return; }
    if (form.type !== "file" && !detectType(form.url)) { toast.error("Enter a valid YouTube or Instagram Reel URL"); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/videos/${editingId}`, form);
        toast.success("Video updated");
      } else {
        await api.post("/videos", form);
        toast.success("Video added");
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save video"); }
    finally { setBusy(false); }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!form.category) { toast.error("Pick a category first, then upload"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, url: r.data.url, type: "file" }));
      toast.success("Video file uploaded — now click Add Video");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({ url: v.url, title: v.title || "", category: v.category, thumbnail: v.thumbnail || "", type: v.type });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await api.delete(`/videos/${id}`);
    toast.success("Video deleted");
    load();
  };

  const isImage = (m) => (m.contentType || "").startsWith("image") || /\.(png|jpe?g|webp|gif|avif|bmp)$/i.test(m.filename || m.url || "");

  const openPicker = async () => {
    setPicker(true);
    setPickerLoading(true);
    try {
      const r = await api.get("/media");
      setMedia(r.data || []);
    } catch {
      setMedia([]);
      toast.error("Could not load your Media Library — please log in again.");
    } finally {
      setPickerLoading(false);
    }
  };

  const uploadThumb = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, thumbnail: r.data.url }));
      setPicker(false);
      toast.success("Thumbnail uploaded and selected");
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusy(false);
      if (thumbRef.current) thumbRef.current.value = "";
    }
  };

  const detected = detectType(form.url);

  if (!data) return <p className="text-slate-400">Loading…</p>;

  return (
    <div data-testid="social-gallery-manager" className="max-w-5xl pb-24">
      <h1 className="font-heading text-2xl font-semibold">Social Media Gallery</h1>
      <p className="text-sm text-slate-500">Categories and videos shown on the Social Media Gallery page (under Media &amp; Press).</p>

      {/* categories */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="video-categories">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Categories</p>
        <div className="flex flex-wrap items-center gap-2">
          {cats.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 py-1.5 pl-4 pr-2 text-sm" data-testid={`vcat-${c.id}`}>
              {renaming?.id === c.id ? (
                <>
                  <input className="w-32 rounded border border-slate-300 px-2 py-0.5 text-sm" value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} data-testid="vcat-rename-input" />
                  <button type="button" onClick={saveRename} aria-label="Save name" className="text-emerald-600 hover:text-emerald-700" data-testid="vcat-rename-save"><Check className="h-4 w-4" /></button>
                  <button type="button" onClick={() => setRenaming(null)} aria-label="Cancel" className="text-slate-400"><X className="h-4 w-4" /></button>
                </>
              ) : (
                <>
                  {c.name}
                  <button type="button" onClick={() => setRenaming({ id: c.id, name: c.name })} aria-label={`Rename ${c.name}`} className="text-slate-400 hover:text-brand-magenta" data-testid={`vcat-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => removeCategory(c)} aria-label={`Delete ${c.name}`} className="text-slate-400 hover:text-red-500" data-testid={`vcat-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </>
              )}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <input className={`${inp} !w-44`} placeholder="New category" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCategory())} data-testid="vcat-new-input" />
            <button type="button" onClick={addCategory} className="inline-flex items-center gap-1 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white hover:opacity-90" data-testid="vcat-add-button">
              <Plus className="h-4 w-4" /> Add
            </button>
          </span>
        </div>
      </div>

      {/* add / edit video */}
      <form onSubmit={submitVideo} className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="video-form">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{editingId ? "Edit Video" : "Add Video"}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-slate-500">Category</label>
            <select className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="video-category-select">
              <option value="">Select category…</option>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-500">Title (optional)</label>
            <input className={inp} placeholder="e.g. Team offsite highlights" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="video-title-input" />
          </div>
        </div>
        <div className="mt-3">
          <label className="mb-1 block text-xs text-slate-500">YouTube or Instagram Reel URL</label>
          <div className="flex items-center gap-2">
            <input className={inp} placeholder="https://www.youtube.com/watch?v=… or https://www.instagram.com/reel/…" value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value, type: "" })} data-testid="video-url-input" />
            {form.type === "file" ? (
              <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600"><Film className="h-3.5 w-3.5" /> File</span>
            ) : detected === "youtube" ? (
              <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600" data-testid="video-type-badge"><Youtube className="h-3.5 w-3.5" /> YouTube</span>
            ) : detected === "instagram" ? (
              <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-pink-50 px-3 py-1.5 text-xs font-semibold text-pink-600" data-testid="video-type-badge"><Instagram className="h-3.5 w-3.5" /> Instagram</span>
            ) : null}
          </div>
          <p className="mt-1.5 text-xs text-slate-400">YouTube thumbnails load automatically. For Instagram Reels that won't play inline, upload the original video file instead:</p>
          <input ref={fileRef} type="file" accept="video/*" onChange={uploadFile} className="hidden" data-testid="video-file-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="mt-2 inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-brand-magenta hover:text-brand-magenta" data-testid="video-upload-button">
            <Upload className="h-3.5 w-3.5" /> Upload video file instead
          </button>
        </div>
        <div className="mt-3">
          <label className="mb-1 block text-xs text-slate-500">Custom thumbnail (optional — overrides the auto one)</label>
          <div className="flex items-center gap-2">
            <input className={inp} placeholder="Image URL from Media Library" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} data-testid="video-thumbnail-input" />
            <button type="button" onClick={openPicker} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-brand-magenta hover:text-brand-magenta" data-testid="video-thumbnail-picker">
              <ImagePlus className="h-3.5 w-3.5" /> Library
            </button>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button type="submit" disabled={busy} className="rounded-full bg-brand-magenta px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60" data-testid="video-save-button">
            {busy ? "Saving…" : editingId ? "Save Changes" : "Add Video"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }} className="text-sm text-slate-500 hover:text-slate-700" data-testid="video-cancel-edit">Cancel edit</button>
          )}
        </div>
      </form>

      {/* videos grid */}
      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="video-admin-grid">
        {videos.map((v, i) => (
          <div key={v.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`video-admin-item-${i}`}>
            <div className="relative aspect-[9/16] bg-brand-ink">
              {v.thumbnail ? (
                <img src={mediaUrl(v.thumbnail)} alt="" className="h-full w-full object-cover" />
              ) : v.type === "youtube" ? (
                <img src={`https://img.youtube.com/vi/${v.video_id}/hqdefault.jpg`} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center text-white/40"><Film className="h-8 w-8" /></span>
              )}
              <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-brand-ink/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                {v.type === "youtube" ? <Youtube className="h-3 w-3" /> : v.type === "instagram" ? <Instagram className="h-3 w-3" /> : <Film className="h-3 w-3" />}
                {v.type}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2 px-3.5 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{v.title || "Untitled video"}</p>
                <p className="text-xs text-slate-400">{v.category}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button type="button" onClick={() => startEdit(v)} aria-label="Edit video" className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-brand-magenta" data-testid={`video-edit-${i}`}><Pencil className="h-4 w-4" /></button>
                <button type="button" onClick={() => removeVideo(v.id)} aria-label="Delete video" className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-500" data-testid={`video-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {videos.length === 0 && (
          <p className="col-span-full rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="video-admin-empty">
            No videos yet. Add your first YouTube or Instagram Reel above.
          </p>
        )}
      </div>

      {/* media picker modal */}
      {picker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPicker(false)} data-testid="video-thumb-modal">
          <div className="max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Pick a thumbnail</h3>
              <button type="button" onClick={() => setPicker(false)} aria-label="Close" className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <input ref={thumbRef} type="file" accept="image/*" onChange={uploadThumb} className="hidden" data-testid="video-thumb-upload-input" />
            {pickerLoading ? (
              <p className="py-10 text-center text-sm text-slate-400">Loading your Media Library…</p>
            ) : (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                <button type="button" onClick={() => thumbRef.current?.click()} disabled={busy} data-testid="video-thumb-upload"
                  className="flex h-24 flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-slate-200 text-xs font-semibold text-slate-500 hover:border-brand-magenta hover:text-brand-magenta">
                  <Upload className="h-5 w-5" /> Upload new
                </button>
                {media.filter(isImage).map((m) => (
                  <button key={m.id || m.url} type="button" onClick={() => { setForm((p) => ({ ...p, thumbnail: m.url })); setPicker(false); }} className="overflow-hidden rounded-lg border border-slate-200 hover:border-brand-magenta" data-testid="video-thumb-option">
                    <img src={mediaUrl(m.url)} alt={m.filename} className="h-24 w-full object-cover" />
                  </button>
                ))}
                {media.filter(isImage).length === 0 && (
                  <p className="col-span-3 py-8 text-center text-sm text-slate-400 sm:col-span-3" data-testid="video-thumb-empty">
                    No images in your Media Library yet — use "Upload new" to add one.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
